import { useEffect, useState } from "react";
import { GitMerge, PencilSimple, Plus, Trash, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { inputCls, labelCls, ReadOnlyNote } from "./shared";

const COLORS = ["#00D62D", "#01B2E0", "#018BFA", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#18181B"];

export default function TagSection({ readOnly }) {
  const [tags, setTags] = useState(null);
  const [modal, setModal] = useState(null); // {id?, name, color}
  const [mergeTarget, setMergeTarget] = useState("");

  useEffect(() => {
    if (!modal) return;
    const onKey = (e) => { if (e.key === "Escape") setModal(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modal]);

  const load = () => api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal.id) await api.patch(`/tags/${modal.id}`, { name: modal.name, color: modal.color });
      else await api.post("/tags", { name: modal.name, color: modal.color });
      toast.success("Tag salvato");
      setModal(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/tags/${t.id}`);
      toast.success(`Tag "${t.name}" eliminato`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const merge = async () => {
    if (!mergeTarget) return;
    try {
      const r = await api.post("/tags/merge", { source_id: modal.id, target_id: mergeTarget });
      toast.success(`Uniti ${r.data.merged_customers} clienti in "${r.data.name}"`);
      setModal(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!tags) return <div className="text-sm text-zinc-400 py-8 text-center">Caricamento...</div>;

  return (
    <div className="space-y-4" data-testid="setsection-tag">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">Tag centralizzati riutilizzabili in CRM, prenotazioni, marketing, coupon, promozioni e fidelity.</p>
        {!readOnly && (
          <button onClick={() => { setModal({ name: "", color: COLORS[3] }); setMergeTarget(""); }}
            className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 shrink-0"
            data-testid="tag-new"><Plus size={14} weight="bold" /> Nuovo tag</button>
        )}
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="tags-list">
        {tags.length === 0 && <div className="px-5 py-8 text-center text-sm text-zinc-400">Nessun tag: creane uno per iniziare.</div>}
        {tags.map((t) => (
          <div key={t.id} className="flex items-center gap-3 px-5 py-3" data-testid={`tag-row-${t.id}`}>
            <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
            <span className="flex-1 text-sm font-semibold text-zinc-900">{t.name}</span>
            <span className="text-xs text-zinc-500">{t.usage} clienti</span>
            {!readOnly && (
              <>
                <button onClick={() => { setModal({ id: t.id, name: t.name, color: t.color }); setMergeTarget(""); }}
                  className="p-2 text-zinc-500 hover:text-zinc-900" data-testid={`tag-edit-${t.id}`}><PencilSimple size={16} /></button>
                <button onClick={() => remove(t)} className="p-2 text-zinc-400 hover:text-rose-600" data-testid={`tag-delete-${t.id}`}><Trash size={16} /></button>
              </>
            )}
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setModal(null)}>
          <form onSubmit={save} className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="tag-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">{modal.id ? "Modifica tag" : "Nuovo tag"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div>
              <label className={labelCls}>Nome</label>
              <input required value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })}
                className={inputCls} data-testid="tag-name" />
            </div>
            <div>
              <label className={labelCls}>Colore</label>
              <div className="flex gap-2">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setModal({ ...modal, color: c })}
                    className={`w-8 h-8 rounded-full border-2 ${modal.color === c ? "border-zinc-900" : "border-transparent"}`}
                    style={{ backgroundColor: c }} data-testid={`tag-color-${c.replace("#", "")}`} />
                ))}
              </div>
            </div>
            <button className="w-full h-11 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="tag-save">
              Salva tag
            </button>
            {modal.id && tags.filter((t) => t.id !== modal.id).length > 0 && (
              <div className="pt-3 border-t border-zinc-100 space-y-2">
                <label className={labelCls}>Unisci in un altro tag</label>
                <div className="flex gap-2">
                  <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}
                    className={inputCls} data-testid="tag-merge-select">
                    <option value="">Seleziona tag di destinazione…</option>
                    {tags.filter((t) => t.id !== modal.id).map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <button type="button" onClick={merge} disabled={!mergeTarget}
                    className="h-11 px-3 rounded-md border border-zinc-300 text-xs font-semibold flex items-center gap-1 disabled:opacity-40"
                    data-testid="tag-merge-btn"><GitMerge size={14} /> Unisci</button>
                </div>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
